import { normalizeError } from '@epihub/core/src/errors';

export type TPromiseOptions = {
  name?: string;
  timeoutMs?: number;
  signal?: AbortSignal;
  onAbort?: () => void;
};

export function promise<T>(
  run: (
    resolve: (t: T) => void,
    reject: (err: Error) => void,
  ) => void,
  opts: TPromiseOptions = {},
): Promise<T> {
  const name = opts.name ?? 'unknown';

  let timeoutId: NodeJS.Timeout | number | null = null;

  const promise = new Promise<T>((resolve, reject) => {
    const cleanup = () => {
      opts.signal?.removeEventListener('abort', onAbort);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };

    const wrappedResolve = (val: T) => {
      cleanup();
      resolve(val);
    };

    const wrappedReject = (err: Error) => {
      cleanup();
      reject(err);
    };

    const onAbort = () => {
      opts.onAbort?.();
      wrappedReject(normalizeError(opts.signal?.reason));
    };

    if (opts.signal?.aborted) {
      onAbort();
    } else if (opts.signal) {
      opts.signal.addEventListener('abort', onAbort);
    }

    if (opts.timeoutMs) {
      timeoutId = setTimeout(() => {
        wrappedReject(new Error(`Promise(${name}) timed out after ${opts.timeoutMs}ms`));
      }, opts.timeoutMs);
    }

    run(wrappedResolve, wrappedReject);
  });

  return promise;
}

export function awaited<T>(run: () => Promise<T>, opts: TPromiseOptions = {}) {
  return promise<T>((resolve, reject) => {
    // This ensures that we are calling .then
    // on somthing that is actually a promise
    (async () => run())()
      .then(resolve)
      .catch((e) => {
        reject(e);
      });
  }, opts);
}

export function isPromise(p: any): p is Promise<any> {
  return !!p && (typeof p === 'object' || typeof p === 'function') && typeof p.then === 'function';
}

export async function reducePromisesSequential<
  T,
  R extends (acc: X, t: T) => Promise<X>,
  X,
>(
  arr: T[],
  func: R,
  init: X,
): Promise<X> {
  return arr.reduce<any>(
    async (previousPromise, t) => {
      const acc = await previousPromise;
      const updatedx = await func(acc, t);
      return updatedx;
    },
    Promise.resolve(init),
  );
}
